<template>
  <v-container pa-0>
    <v-dialog v-model="showDialog" max-width="750" click:outside="" persistent>
      <v-card class='card'>
        <v-card-title>
          <h3>{{ $vuetify.t('$vuetify.RoleEdit.userRoleEditTile', user.username) }}</h3>
        </v-card-title>
        <v-card-text>
          <v-card class="mb-2">
            <v-card-title class="pb-0">
              <h4>{{ $vuetify.t('$vuetify.RoleEdit.userRoleAssigned') }}</h4>
            </v-card-title>
            <v-card-text>
              <v-list two-line dense>
                <template v-for="role in assignedRoles">
                  <v-divider/>
                  <v-list-tile>
                    <v-list-tile-content>
                      <v-list-tile-title>
                        {{ getNameOfAssignedRole(role) }}
                      </v-list-tile-title>
                      <v-list-tile-sub-title v-if="role.idea_space != null">
                        {{ getNameOfIdeaSpace(role.idea_space) }}
                      </v-list-tile-sub-title>
                    </v-list-tile-content>

                    <v-list-tile-action>
                      <v-btn icon ripple @click="() => remove(role.role_id, role.idea_space)">
                        <v-icon color="grey lighten-1">delete</v-icon>
                      </v-btn>
                    </v-list-tile-action>
                  </v-list-tile>
                </template>
                <v-divider/>
              </v-list>
              <v-layout v-if="allValidityWarning.isActive" row no-wrap class="mt-4">
                <v-flex sm12>
                  <v-icon style="display: table-cell;">info</v-icon>
                  <p style="display:table-cell; vertical-align: middle;">
                    {{ $vuetify.t('$vuetify.RoleEdit.allValidityWarning') }}
                  </p>
                  <p style="margin-left: 32px">
                    {{ $vuetify.t('$vuetify.RoleEdit.allValidityAffectedRoles') }}
                    {{ allValidityWarning.forRoles.join(', ') }}
                  </p>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
          <v-card sm12>
            <v-card-title class="pb-0">
              <h4>{{ $vuetify.t('$vuetify.RoleEdit.assignRole') }}</h4>
            </v-card-title>
            <v-card-text>
              <v-layout row no-wrap>
                <v-flex xs12 sm6 class="mr-2">
                  <v-select
                    v-model="form.selectedRole"
                    v-validate="'required'"
                    @change="form.ideaSpace = null"
                    :error-messages="errors.collect('usergroup')"
                    :items="userRoles"
                    :label="this.$vuetify.t('$vuetify.AdminUsers.formGroup')"
                    item-text="name"
                    item-value="id"
                    required
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-select
                    :disabled="!hasRoleIdeaSpaceValidity"
                    v-model="form.ideaSpace"
                    v-validate=""
                    :error-messages="errors.collect('ideaSpace')"
                    :items="ideaSpaces"
                    :label="this.$vuetify.t('$vuetify.AdminUsers.formIdeaSpace')"
                    name='ideaSpace'
                  ></v-select>
                </v-flex>
                <v-flex class='text-xs-right'>
                  <v-btn :disabled="isRoleAlreadyAssigned || !form.selectedRole" color="primary" @click="assignRole">
                    {{ $vuetify.t('$vuetify.AdminUsers.formAddRole') }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>

        </v-card-text>
        <v-card-actions>
          <v-btn
            @click="closeDialog"
          >{{ $vuetify.t('$vuetify.AdminUsers.formClose') }}
          </v-btn>
        </v-card-actions>
        <v-snackbar
          v-model="showSnackbar"
          :bottom="true"
        >
          {{ snackbarMsg }}
          <v-btn
            color="pink"
            flat
            @click="showSnackbar = false"
          >
            {{ $vuetify.t('$vuetify.Snackbar.close') }}
          </v-btn>
        </v-snackbar>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from '@/api'
import {getIdeaSpaces} from '../../utils/ideaSpaces'
import {getUserRoles} from '../../utils/userRoles'

export default {
  $_veeValidate: {validator: 'new'},
  props: {
    show: Boolean,
    user: Object
  },
  name: 'UserRoleEditDialog',
  data: function () {
    return {
      allValidityWarning: {
        isActive: false,
        forRoles: []
      },
      groups: {},
      ideaSpaces: [],
      userRoles: [],
      assignedRoles: [],
      form: {
        selectedRole: null,
        ideaSpace: null
      },
      showSnackbar: false,
      snackbarMsg: ''
    }
  },

  beforeMount: async function () {
    if (this.user == null) throw Error('GroupEditor requires a user prop')
    this.userRoles = await getUserRoles()
    this.ideaSpaces = await getIdeaSpaces(this.$store.getters.selected_school, this.$vuetify.t)
    this.getAssignedRoles()
  },

  computed: {
    showDialog: {
      get () {
        return this.show
      },
      set (value) {
        this.$emit('handleClose', value)
        this.$emit('reloadUsers')
      }
    },
    schoolId: function () {
      return this.$store.getters.selected_school
    },
    hasRoleIdeaSpaceValidity: function () {
      if (this.form.selectedRole) {
        let selectedRolePermissions = this.userRoles.find(role => role.id === this.form.selectedRole).permissions
        return this.$hasValidity(selectedRolePermissions, this.$validity.idea_space) ||
          this.$hasValidity(selectedRolePermissions, this.$validity.school_and_idea_space)
      }
      return false
    },
    isRoleAlreadyAssigned: function () {
      if (this.form.selectedRole) {
        return !!this.assignedRoles.find(role => role.role_id === this.form.selectedRole &&
          role.idea_space === this.form.ideaSpace)
      }
    }
  },

  methods: {
    closeDialog: function () {
      this.showDialog = false
    },
    getAssignedRoles: function () {
      api.userRole.getAssignedRoles(this.user.id, this.schoolId).then(res => {
        if (res.status < 400) {
          this.assignedRoles = res.data
          this.allValidityWarning.isActive = false
          this.allValidityWarning.forRoles = []

          if (this.assignedRoles.length > 0) {
            this.assignedRoles.forEach(assignedRole => {
              let assignedCustomRole = this.userRoles.find(role => role.id === assignedRole.role_id)
              if (this.$hasValidity(assignedCustomRole.permissions, this.$validity.all)) {
                this.allValidityWarning.isActive = true
                this.allValidityWarning.forRoles.push(assignedCustomRole.name)
                return false
              }
            })
          }
        } else {
          this.assignedRole = []
        }
      })
    },
    getNameOfAssignedRole: function (role) {
      return this.userRoles.find(userRole => userRole.id === role.role_id).name
    },
    getNameOfIdeaSpace: function (id) {
      return this.ideaSpaces.find(ideaSpace => ideaSpace.value === id).text
    },
    remove: function (roleId, ideaSpace) {
      api.userRole.deleteRole(this.user.id, roleId, this.schoolId, ideaSpace)
        .then((res) => {
          this.showSnackbar = true
          this.snackbarMsg = this.$vuetify.t(
            '$vuetify.AdminUsers.snackbarRoleDeleted')
          this.getAssignedRoles()
          // this.handleSuccess()
        })
    },
    assignRole: function () {
      const role = {
        school_id: this.schoolId,
        user_id: this.user.id,
        role_id: this.form.selectedRole,
        idea_space: this.form.ideaSpace || null
      }

      api.userRole.assignRole(role).then((res) => {
        if (res.status < 400) {
          this.showSnackbar = true
          this.snackbarMsg = this.$vuetify.t(
            '$vuetify.AdminUsers.snackbarSaved')
          this.getAssignedRoles()
          // this.handleSuccess(res.data)
        }
      }).catch((err) => {
        // 23505 is the Postgrest error code for violations of
        // unique constraints
        if (err.response && err.response.data.code === '23505') {
          this.showSnackbar = true
          this.snackbarMsg = this.$vuetify.t(
            '$vuetify.AdminUsers.snackbarDuplicateGroup')
        } else {
          this.showSnackbar = true
          this.snackbarMsg = this.$vuetify.t(
            '$vuetify.Snackbar.clientError')
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.card {
  padding: 10px;
}

.v-list__tile {
  background: #CCC
}

</style>
